import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, getDay, startOfWeek, endOfWeek, addMonths, subMonths } from 'date-fns';
import { Droplets, Star, Heart, ChevronLeft, ChevronRight, Moon, Filter } from 'lucide-react';
import { usePeriodStore } from '../../store/periodStore';
import { PeriodLogDialog } from './PeriodLogDialog';

type ViewFilter = 'all' | 'period' | 'ovulation' | 'fertile' | 'predictions';

export function CycleCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ViewFilter>('all');
  const { getPeriodLog, logPeriod, removePeriodLog, predictNextPeriod, predictOvulation } = usePeriodStore();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const nextPeriod = predictNextPeriod();
  const ovulation = predictOvulation();

  const filters: { id: ViewFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'period', label: 'Period' },
    { id: 'ovulation', label: 'Ovulation' },
    { id: 'fertile', label: 'Fertile' },
    { id: 'predictions', label: 'Predictions' }
  ];

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setShowLogDialog(true);
  };

  const handleLogPeriod = (flow: 'light' | 'medium' | 'heavy', notes?: string) => {
    if (!selectedDate) return;
    logPeriod(selectedDate.toISOString(), flow, notes);
    setShowLogDialog(false);
  };

  const getFlowIcon = (flow: 'light' | 'medium' | 'heavy') => {
    switch (flow) {
      case 'light':
        return <Droplets className="w-3 h-3 text-red-500" />;
      case 'medium':
        return (
          <div className="flex">
            <Droplets className="w-3 h-3 text-red-500" />
            <Droplets className="w-3 h-3 text-red-500 -ml-1" />
          </div>
        );
      case 'heavy':
        return (
          <div className="flex">
            <Droplets className="w-3 h-3 text-red-500" />
            <Droplets className="w-3 h-3 text-red-500 -ml-1" />
            <Droplets className="w-3 h-3 text-red-500 -ml-1" />
          </div>
        );
    }
  };

  const shouldShowDate = (day: Date) => {
    const log = getPeriodLog(day.toISOString());
    const isPredictedPeriod = nextPeriod && isSameDay(day, nextPeriod);
    const isOvulation = ovulation && isSameDay(day, ovulation);
    const isFertile = ovulation && 
      Math.abs((day.getTime() - ovulation.getTime()) / (1000 * 60 * 60 * 24)) <= 3;

    switch (activeFilter) {
      case 'period':
        return !!log;
      case 'ovulation':
        return isOvulation;
      case 'fertile':
        return isFertile;
      case 'predictions':
        return isPredictedPeriod || isOvulation;
      default:
        return true;
    }
  };

  const getDayStyle = (day: Date) => {
    const log = getPeriodLog(day.toISOString());
    const isSelected = selectedDate && isSameDay(day, selectedDate);
    const isTodays = isToday(day);
    const isCurrentMonth = isSameMonth(day, currentDate);
    const isPredictedPeriod = nextPeriod && isSameDay(day, nextPeriod);
    const isOvulation = ovulation && isSameDay(day, ovulation);
    const isFertile = ovulation && 
      Math.abs((day.getTime() - ovulation.getTime()) / (1000 * 60 * 60 * 24)) <= 3;

    let styles = {
      base: "relative w-full h-12 flex flex-col items-center justify-between p-1 transition-all duration-300",
      background: "bg-transparent",
      text: isCurrentMonth ? "text-foreground" : "text-muted-foreground/50",
      icon: null as React.ReactNode | null,
      border: ""
    };

    if (!shouldShowDate(day)) {
      styles.text += " opacity-30";
    }

    if (log) {
      styles.icon = getFlowIcon(log.flow);
    } else if (isPredictedPeriod) {
      styles.icon = <Moon className="w-3 h-3 text-primary animate-pulse" />;
    } else if (isOvulation) {
      styles.icon = <Star className="w-3 h-3 text-purple-500 animate-spin-slow" />;
    } else if (isFertile) {
      styles.icon = <Heart className="w-3 h-3 text-pink-400 animate-pulse" />;
    }

    if (isTodays) {
      styles.border = "ring-2 ring-primary rounded-lg";
    }

    if (isSelected) {
      styles.background = "bg-primary/10";
      styles.border = "ring-2 ring-primary rounded-lg";
    }

    return styles;
  };

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex gap-2 overflow-x-auto pb-2 theme-scrollbar">
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
              activeFilter === filter.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between px-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => handleMonthChange('prev')}
          className="p-2 rounded-full hover:bg-muted/50 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-primary" />
        </motion.button>
        
        <h3 className="text-lg font-semibold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent">
          {format(currentDate, 'MMMM yyyy')}
        </h3>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => handleMonthChange('next')}
          className="p-2 rounded-full hover:bg-muted/50 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-primary" />
        </motion.button>
      </div>

      {/* Calendar Grid */}
      <div className="theme-card p-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent opacity-50" />
        
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 mb-2">
          {weekDays.map((day, idx) => (
            <div
              key={`header-${idx}`}
              className="text-center text-xs font-medium text-muted-foreground py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day) => {
            const styles = getDayStyle(day);
            return (
              <motion.button
                key={day.toISOString()}
                onClick={() => handleDateClick(day)}
                className={`${styles.base} ${styles.background} ${styles.border}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className={`text-sm ${styles.text}`}>
                  {format(day, 'd')}
                </span>
                {styles.icon && (
                  <div className="mt-1">
                    {styles.icon}
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <div className="flex">
            <Droplets className="w-3 h-3 text-red-500" />
            <Droplets className="w-3 h-3 text-red-500 -ml-1" />
            <Droplets className="w-3 h-3 text-red-500 -ml-1" />
          </div>
          <span>Period</span>
        </div>
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 text-purple-500" />
          <span>Ovulation</span>
        </div>
        <div className="flex items-center gap-1">
          <Heart className="w-3 h-3 text-pink-400" />
          <span>Fertile</span>
        </div>
        <div className="flex items-center gap-1">
          <Moon className="w-3 h-3 text-primary" />
          <span>Predicted</span>
        </div>
      </div>

      {/* Period Log Dialog */}
      {showLogDialog && selectedDate && (
        <PeriodLogDialog
          date={selectedDate}
          onClose={() => setShowLogDialog(false)}
          onLog={handleLogPeriod}
          existingLog={getPeriodLog(selectedDate.toISOString())}
          onRemove={() => {
            if (selectedDate) {
              removePeriodLog(selectedDate.toISOString());
              setShowLogDialog(false);
            }
          }}
        />
      )}
    </div>
  );
}